import { schGenerateCommentaryField } from '@aloft/toolkit-shared';
import type { SizeProperty } from '@aloft/toolkit-sui';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Textarea } from '@aloft/toolkit-sui/chakra';

import { useCallback, useState } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';

import { useLocalStorage } from '../../controller/hooks/useLocalStorage.ts';
import { useRequiredFieldAlertInteraction } from '../../modules/alerts/useRequiredFieldAlertInteraction.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import {
  ALERT_TARGET_FIELD_TITLE,
  getAlertTargetInputFieldSx,
} from '../PropertyGrid/fields/styles.ts';
import { FormGenerateCommentaryButton } from './GenerateCommentaryButton.tsx';
import { shouldShowGenerateCommentary } from './generateCommentaryGate.ts';

import '../../modules/alerts/requiredFieldAlertHighlight.scss';
import './formTextareaGenerating.scss';

const generateCommentaryField = (requiredFieldName: string | undefined, name: string) => {
  const parsed = schGenerateCommentaryField.safeParse((requiredFieldName ?? name).split('.')[0]);
  if (!parsed.success) return undefined;
  return { field: parsed.data };
};

type FormTextareaProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  labelWidth?: string;
  column?: boolean;
  inputWidth?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  resize?: 'none' | 'horizontal' | 'vertical' | 'both';
  customErrorMessage?: string;
  onClear?: () => void;
  labelVariant?: 'h4' | 'h3' | 'label';
  labelColor?: 'textSecondary' | 'textTertiary' | 'textPrimary';
  height?: string;
  defaultHeight?: string;
  persistedHeightKey?: string;
  fillAvailable?: boolean;
  subHeaderContent?: React.ReactNode;
  requiredFieldName?: string;
};

const RESIZE_HANDLE_HIT_PX = 20;
const MIN_TEXTAREA_HEIGHT_PX = 80;
const AUTO_SCROLL_EDGE_PX = 48;
const AUTO_SCROLL_STEP_PX = 24;

const findScrollParent = (el: HTMLElement): HTMLElement | null => {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

const isVerticalResizeHandle = (e: Pick<PointerEvent, 'clientX' | 'clientY'>, el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  return e.clientY >= rect.bottom - RESIZE_HANDLE_HIT_PX;
};

export const FormTextarea = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  isRequired = false,
  labelWidth = '250px',
  column = false,
  inputWidth = '500px',
  size = 'sm',
  resize = 'vertical',
  customErrorMessage,
  onClear,
  labelVariant = 'h4',
  labelColor = 'textSecondary',
  height,
  defaultHeight,
  persistedHeightKey,
  fillAvailable = false,
  subHeaderContent,
  requiredFieldName,
}: FormTextareaProps<T>) => {
  const { containerRef, isAlertTarget, isAlertHighlighted, clearAlertTarget } =
    useRequiredFieldAlertInteraction(requiredFieldName ?? '');
  const isAdmin = useAppSelector((state) => !!state.account.isAdmin);
  const generateField = generateCommentaryField(requiredFieldName, String(name));
  const showGenerateButton =
    generateField != null && shouldShowGenerateCommentary(isAdmin);
  const fieldValue = useWatch({
    control,
    name,
    disabled: !requiredFieldName && !showGenerateButton,
  });
  const isFieldComplete = Boolean(fieldValue?.toString().trim());
  const showAlertHighlight = Boolean(requiredFieldName && isAlertHighlighted && !isFieldComplete);

  const [storedHeight, setStoredHeight] = useLocalStorage<string | null>({
    key: persistedHeightKey ?? `textareaHeight:noop:${String(name)}`,
    defaultValue: null,
  });
  const [dragHeight, setDragHeight] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const handleGeneratingChange = useCallback((generating: boolean) => {
    setIsGenerating(generating);
  }, []);

  const persistedHeight = persistedHeightKey ? (dragHeight ?? storedHeight) : null;

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLTextAreaElement>) => {
      if (!persistedHeightKey) return;
      const el = e.currentTarget;
      if (!isVerticalResizeHandle(e, el)) return;

      e.preventDefault();
      const startY = e.clientY;
      const startHeight = el.offsetHeight;
      const scrollParent = findScrollParent(el);
      const startScrollTop = scrollParent?.scrollTop ?? 0;
      let latestHeight = `${startHeight}px`;

      const onMove = (ev: PointerEvent) => {
        const scrollDelta = (scrollParent?.scrollTop ?? 0) - startScrollTop;
        const nextHeight = Math.max(
          MIN_TEXTAREA_HEIGHT_PX,
          startHeight + (ev.clientY - startY) + scrollDelta,
        );
        latestHeight = `${nextHeight}px`;
        setDragHeight(latestHeight);

        if (scrollParent) {
          const parentRect = scrollParent.getBoundingClientRect();
          if (ev.clientY > parentRect.bottom - AUTO_SCROLL_EDGE_PX) {
            scrollParent.scrollTop += AUTO_SCROLL_STEP_PX;
          } else if (ev.clientY < parentRect.top + AUTO_SCROLL_EDGE_PX) {
            scrollParent.scrollTop -= AUTO_SCROLL_STEP_PX;
          }
        }
      };

      const onUp = () => {
        setStoredHeight(latestHeight);
        setDragHeight(null);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [persistedHeightKey, setStoredHeight],
  );

  const isFilling = fillAvailable && !persistedHeight;
  const appliedHeight =
    persistedHeight ?? height ?? defaultHeight ?? (fillAvailable ? '100%' : undefined);

  const content = (
    <Flex
      flexDirection={column ? 'column' : 'row'}
      flex={isFilling ? 1 : undefined}
      minHeight={isFilling ? 0 : undefined}
      height={isFilling ? '100%' : undefined}
    >
      <Block
        width={column ? '100%' : (labelWidth as SizeProperty)}
        marginBottom={column ? '0.5rem' : undefined}
        flex={isFilling ? 0 : undefined}
      >
        {(label || showGenerateButton) && (
          <Flex alignItems="center" justifyContent="space-between" gap={1}>
            {label && (
              <Typography variant={labelVariant} color={labelColor}>
                <>
                  {label}
                  {isRequired && <span style={{ color: 'red' }}> *</span>}
                </>
              </Typography>
            )}
            {generateField && showGenerateButton && (
              <FormGenerateCommentaryButton
                field={generateField.field}
                formFieldName={String(name)}
                value={fieldValue?.toString()}
                onGeneratingChange={handleGeneratingChange}
              />
            )}
          </Flex>
        )}
      </Block>
      {subHeaderContent}
      <Controller
        name={name}
        control={control}
        rules={{
          required: isRequired,
        }}
        render={({ field, fieldState }) => {
          const showAlertStyle = showAlertHighlight;
          const formError = fieldState.error?.message;
          const customError = customErrorMessage;
          const hasError = formError || customError;
          const shouldShowError =
            (fieldState.isTouched && hasError) ||
            (Boolean(requiredFieldName && isAlertHighlighted) && hasError);
          const fieldAlertSx = showAlertStyle
            ? getAlertTargetInputFieldSx(true, 'error', true)
            : undefined;
          const { ref: fieldRef, ...fieldProps } = field;

          return (
            <Block
              className={isGenerating ? 'form-textarea-generating' : undefined}
              width={column ? '100%' : (inputWidth as SizeProperty)}
              flex={isFilling ? 1 : undefined}
              minHeight={isFilling ? 0 : undefined}
              height={isFilling ? '100%' : undefined}
            >
              <Textarea
                size={size}
                placeholder={placeholder}
                isInvalid={!!shouldShowError}
                isReadOnly={isGenerating}
                sx={isFilling ? { ...fieldAlertSx, height: '100%', minHeight: 0 } : fieldAlertSx}
                title={showAlertStyle ? ALERT_TARGET_FIELD_TITLE : undefined}
                height={appliedHeight}
                resize={isGenerating ? 'none' : resize}
                {...fieldProps}
                value={isGenerating ? 'Generating…' : fieldProps.value}
                ref={fieldRef}
                onFocus={() => {
                  if (isAlertTarget) {
                    clearAlertTarget();
                  }
                }}
                onBlur={field.onBlur}
                onPointerDown={isGenerating ? undefined : handleResizePointerDown}
                onChange={(ev) => {
                  if (isGenerating) return;
                  field.onChange(ev);
                  if (onClear && ev.target.value === '') {
                    onClear();
                  }
                }}
              />
              {shouldShowError && (
                <Typography variant="body2" color="textError">
                  {formError || customError}
                </Typography>
              )}
            </Block>
          );
        }}
      />
    </Flex>
  );

  if (requiredFieldName) {
    return (
      <Block
        ref={containerRef}
        data-required-field={requiredFieldName}
        data-required-field-alert-highlight={showAlertHighlight ? '' : undefined}
        flex={isFilling ? 1 : undefined}
        minHeight={isFilling ? 0 : undefined}
        height={isFilling ? '100%' : undefined}
      >
        {content}
      </Block>
    );
  }

  return content;
};
